import { apiBlob, apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import { exportQuery } from '@/lib/download-file';
import type {
  ConversionRateParams,
  ConversionRateResponse,
  CreateSalesOrderFromQuoteInput,
  Quotation,
  QuotationListParams,
  QuotationListResponse,
  QuotationRefundMode,
  QuotationSalesOrderRef,
  SendQuotationInput,
  SendQuotationResult,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_LIST: QuotationListResponse = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};


export const quotationsApi = {
  list: (params: QuotationListParams) =>
    withFallback(
      () =>
        apiClientPaginated<QuotationListResponse['data']>(
          `/quotations${buildQuery({
            page: params.page,
            limit: params.limit,
            location_id: params.location_id,
            status: params.status || undefined,
            search: params.search,
            customer_name: params.customer_name,
            mobile: params.mobile,
            quote_number: params.quote_number,
            start_date: params.start_date,
            end_date: params.end_date,
            sort: params.sort,
          })}`,
        ) as Promise<QuotationListResponse>,
      EMPTY_LIST,
    ),

  // Every quote the filters match as CSV, written by the API from all pages
  // (T27; #205 was the export reusing the 20-row page).
  exportCsv: (params: Omit<QuotationListParams, 'page' | 'limit'>) =>
    apiBlob(`/quotations/export${exportQuery(params as Record<string, string | undefined>)}`),

  getById: (id: string) =>
    withFallback(() => apiClient<Quotation>(`/quotations/${id}`), null),

  // Conversion goes through the sales-orders module: the customer's PO is
  // recorded and the quote becomes a Sales Order (deliveries, invoices and
  // payments are tracked there). The returned ref is enough to navigate.
  createSalesOrder: (input: CreateSalesOrderFromQuoteInput) =>
    apiClient<QuotationSalesOrderRef>('/sales-orders', {
      method: 'POST',
      body: input,
    }),

  send: (id: string, input: SendQuotationInput) =>
    apiClient<SendQuotationResult>(`/quotations/${id}/send`, {
      method: 'POST',
      body: input,
    }),

  // refund_mode is required by the API when the quote holds a down payment.
  // Below MANAGER, approval_token is a manager's QUOTE_CANCEL approval (SS4-005).
  cancel: (id: string, refundMode?: QuotationRefundMode, approvalToken?: string) =>
    apiClient<{ id: string; status: string; updated_at: string }>(
      `/quotations/${id}/cancel`,
      {
        method: 'POST',
        body:
          refundMode || approvalToken
            ? {
                ...(refundMode ? { refund_mode: refundMode } : {}),
                ...(approvalToken ? { approval_token: approvalToken } : {}),
              }
            : undefined,
      },
    ),

  // Do NOT wrap in withFallback: the dashboard is built entirely on this
  // response, so swallowing a 404/501 into a zeroed payload made a failed load
  // indistinguishable from a genuinely empty pipeline (issue #209). Let the
  // error propagate so the page can surface a real error state with retry.
  conversionRate: (params: ConversionRateParams) =>
    apiClient<ConversionRateResponse>(
      `/quotations/conversion-rate${buildQuery({
        location_id: params.location_id,
        created_by: params.created_by,
        start_date: params.start_date,
        end_date: params.end_date,
      })}`,
    ),

  listChangeRequests: (id: string) =>
    withFallback(
      () =>
        apiClient<QuotationChangeRequest[]>(
          `/quotations/${id}/change-requests`,
        ),
      [] as QuotationChangeRequest[],
    ),

  listSends: (id: string) =>
    withFallback(
      () => apiClient<QuotationSend[]>(`/quotations/${id}/sends`),
      [] as QuotationSend[],
    ),
};

/** One send of a quote (communication_logs row); LINK = approval link shared. */
export interface QuotationSend {
  id: string;
  channel: 'LINK' | 'WHATSAPP' | 'EMAIL' | string;
  recipient: string;
  status: string;
  created_at: string;
}

export interface QuotationChangeRequest {
  id: string;
  notes: string;
  created_at: string;
}
